import { DeskCheckBoxSetsProps, DeskCheckBoxSetsComponents, CodeItem, CodesFilter, StyledPropsTheme } from '@my-types';
import { React, Styled } from '@common/3rd';
import { StyledComponents } from '@styles';
import { CheckableWidget, DeskComponentRegistry } from '@component';
import { Utils } from '@common';
import { ErrorReminder, PassReminder } from './input-reminder';

/**
 * 由于组件第一次构建的时候, model可能还没有值, 导致使用defaultChecked属性是false或者undefined.
 * 之后model值构建完成之后, 更新defaultChecked不能产生效果, 导致第一次onChange不能够正确响应.
 * 因此, 改用didMount和didUpdate之后直接dom设置checked值来替代defaultChecked属性.
 */
class DeskCheckBoxSets<
    P extends DeskCheckBoxSetsProps & { codesFilter?: CodesFilter, includesCodes?:CodeItem[] },
    S,
    C extends DeskCheckBoxSetsComponents
    > extends CheckableWidget<P, S, C> {
    //   checkboxRef: React.RefObject<any>;
    checkboxContainerRef: React.RefObject<any>;
    checkboxRefs: HTMLInputElement[];

    constructor(props: any, context?: any) {
        super(props, context);
        this.checkboxContainerRef = React.createRef();
        this.checkboxRefs = [];
    }
    componentDidMount() {
        super.componentDidMount();
        // this.checkboxRef.current.checked = !!this.getValueFromModel();
        (this.checkboxRefs as HTMLInputElement[]).forEach(ref => {
            if (ref && this.getValueFromModel() && this.getValueFromModel().length) {
                ref.checked = this.getValueFromModel().includes(ref.value);
            }
        });
    }
    componentDidUpdate(prevProps: any, prevState?: any, prevContext?: any) {
        super.componentDidUpdate(prevProps, prevState, prevContext);
        // this.checkboxRef.current.checked = !!this.getValueFromModel();
        (this.checkboxRefs as HTMLInputElement[]).forEach(ref => {
            if (ref && this.getValueFromModel() && this.getValueFromModel().length) {
                ref.checked = this.getValueFromModel().includes(ref.value);
            }
        });
    }
    protected initComponents(): C {
        return {
            CheckboxSetsContainer: Styled.div.attrs({
                'data-widget': 'desk-checkbox-sets-box'
            })`
                display: flex;
                flex-wrap: wrap;
                max-width: ${props => props.theme.INPUT_MAX_WIDTH}px;
                ${props => this.paintColor(props.theme)}
                ${props => this.renderCSS(props.theme)}
            `,
            Box: Styled.div.attrs({
                'data-widget': 'desk-checkbox-box'
            })`
                display: flex;
				margin-left: 16px;
                &.hide {
                    display: none;
                }
            `,
            Checkbox: Styled(StyledComponents.CHECKBOX).attrs({
                'data-widget': 'desk-checkbox-sets'
            })`
            `,
            CheckboxLabel: Styled.div.attrs({
                'data-widget': 'desk-checkbox-sets-label'
            })`
            `
        } as C;
    }
    render() {
        const C = this.getComponents();
        return <C.CheckboxSetsContainer ref={this.checkboxContainerRef}>
            {this.renderOptions()}
            {this.renderReminder()}
        </C.CheckboxSetsContainer >
    }
    protected paintColor(theme: StyledPropsTheme): string {
        return this.isCheckFailOnWhatever() ? 
        `color: ${theme.COLOR_ERROR};margin-bottom:40px;> i > span{top: ${this.checkboxContainerRef?.current.clientHeight}px}` : '';
	}
    renderOptions(): JSX.Element[] {
        const C = this.getComponents();
        return Utils.toArray(this.getCodes()).map((code, index) => {
            return (
                <C.Box key={code.v + ''} className={ this.props.includesCodes && !this.props.includesCodes.some(c => c.v === code.v) ? 'hide' : ''}>
                    <C.Checkbox
                        onChange={this.onValueChanged}
                        onFocus={this.onInputFocus}
                        onBlur={this.onInputBlur}
                        disabled={!this.isEditable()}
                        value={`${code.v}`}
                        ref={this.setRef}
                        name={this.getPropName()}
                    >
                    </C.Checkbox>
                    <C.CheckboxLabel>{`${code.l}`}</C.CheckboxLabel>
                </C.Box>
            );
        });
    }
    protected doAfterCheckCompleted(): this {
		if (this.props.labellingParent) {
			this.props.labellingParent.onCheckCompleted(!this.isCheckFailOnWhatever());
		}
		return this;
    }
    renderReminder(): JSX.Element | void {
		if (this.isCheckFailOnWhatever()) {
			// 有校验失败信息
			return <ErrorReminder msg={this.getCheckFailMessage()!} />;
		} else if (this.isCheckedAnyway()) {
			// 没有校验信息, 但是曾经校验过
			return <PassReminder />;
		}
	}
    private setRef = (ref: any) => {
        this.checkboxRefs.push(ref);
    }
    protected setValueToDOM(value: any, propName?: string): this {
        if (propName == null || propName === this.getPropName()) {
            // this.checkboxRef.current.checked = value ? true : false;
            this.checkboxRefs.forEach(ref => {
                if (ref) {
                    ref.checked = ref && value.includes(ref.value);
                }
            })
            this.forceUpdate();
        }
        return this;
    }
    protected isEditable(): boolean {
        const props = this.props as any;
        return !(props.readonly || props.disabled);
    }
    private onValueChanged = (): void => {
        // const value = this.checkboxRef.current.checked;
        const checked = this.checkboxRefs.filter(ref => ref?.checked);
        const value = checked.map(input => input.value);
        const oldValue = this.getValueFromModel();
        // if (value === oldValue) {
        if (JSON.stringify(value) === JSON.stringify(oldValue)) {
            // 没有变化, 什么都不做
            return;
        }

        this.setValueToModel(value).checkData();
    };
    private onInputFocus = (): void => {
        if (this.props.labellingParent) {
            this.props.labellingParent.onFocusGained();
        }
    };
    private onInputBlur = (): void => {
        if (this.props.labellingParent) {
            this.props.labellingParent.onFocusLost();
        }
    };
    focus(): this {
        // this.checkboxRef.current.focus();
        this.checkboxRefs[0].focus();
        return this;
    }
    getCodes(): CodeItem[] {
        const codesFilter = this.getCodesFilter();
        if (codesFilter) {
            if (Utils.isFunction(codesFilter)) {
                return codesFilter.call(this, this.props.codes, this);
            } else {
                return codesFilter.filter.call(this, this.props.codes, this);
            }
        } else {
            return Utils.toArray(this.props.codes);
        }
    }
    protected getCodesFilter(): CodesFilter | undefined {
		return this.props.codesFilter;
	}
}

DeskComponentRegistry.register({
    checkboxSets: DeskCheckBoxSets
});

export { DeskCheckBoxSets };
